<script setup lang="ts">
import ChatMessage from "~/components/Chat/ChatMessage.vue";
import { useChatStore } from "~/stores/chat/store";
import { useScroll } from "@vueuse/core";

defineProps({
  videoId: {
    type: String,
    required: true,
  },
});

const chatStore = useChatStore();
const { messages, stickers } = storeToRefs(chatStore);

const toast = useToast();

const autoScrollEnabled = ref(true);

const blockedUsers = ref<string[]>([]);

const historyMessagesAfterBlocked = computed(() => {
  return messages.value.filter(
    (message) => !blockedUsers.value.includes(message.sender),
  );
});

const chatContainer = ref<HTMLElement | null>(null);
const { directions, arrivedState } = useScroll(chatContainer, {
  behavior: "smooth",
});

function scrollToBottom() {
  if (chatContainer.value && autoScrollEnabled.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
}

function onBlockUser(userId: string) {
  blockedUsers.value.push(userId);

  toast.add({
    severity: "warn",
    summary: "User blocked !",
    detail: `User ${userId} has been blocked`,
    life: 3000,
  });
}

function handleScrollToBottomClick() {
  autoScrollEnabled.value = true;
  scrollToBottom();
}

watch(
  () => historyMessagesAfterBlocked.value.length,
  async () => {
    await nextTick();
    scrollToBottom();
  },
  { immediate: true },
);

watch(
  () => directions?.top,
  () => {
    if (directions?.top) {
      autoScrollEnabled.value = false;
    }
  },
);

watch(
  () => arrivedState?.bottom,
  () => {
    if (arrivedState?.bottom) {
      autoScrollEnabled.value = true;
    }
  },
);
</script>

<template>
  <div
    class="relative flex h-full flex-grow flex-col overflow-y-hidden bg-black p-3"
  >
    <div class="">Welcome to the poker chat room !</div>
    <div
      ref="chatContainer"
      class="mt-2 flex flex-grow flex-col gap-2 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-400"
    >
      <div v-for="(item, index) in historyMessagesAfterBlocked" :key="index">
        <ChatMessage
          :stickers="stickers"
          :message="item"
          :video-id="videoId"
          @block-user="onBlockUser"
        />
      </div>
    </div>
    <div
      v-if="!autoScrollEnabled"
      class="absolute bottom-2 left-1/2 flex h-9 w-9 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full bg-primary"
      @click="handleScrollToBottomClick"
    >
      <font-awesome-icon :icon="['fas', 'arrow-down']" />
    </div>
  </div>
</template>

<style scoped></style>
